import { normalize } from 'normalizr';
import { ActionTree } from 'vuex';
import {
  comment as commentSchema,
  content as contentSchema,
  exerciseCommentAttachments as exerciseCommentAttachmentsSchema,
  text as textSchema,
} from '@descomplica/dex-store/lib/normalizr_schema';
import { dexMutation } from '@/helpers/graphql';
import { CommentState } from './state';
import RootState from '@/store/root_state';
import {
  createNestedCommentTextAndContent,
  createUserDiscursiveExerciseComment,
  createUserThinkAndAnswerComment,
  updateCommentById,
  updateUserPracticeActivityComment,
} from '../../queries/dexQueries';
import { ErrorHandler } from '@/helpers/errorHandler';
import i18n from '../../../i18n';
import { UnifiedCommentModel } from '@/models/UnifiedCommentModel';
import { ActivityType, UndergradGradesService } from '@/services/UndergradGrades';

const actions: ActionTree<CommentState, RootState> = {
  async CREATE_THINK_AND_ANSWER_COMMENT({ commit }, payload) {
    try {
      const { questionId, text, isGradeActivitiesViaAi } = payload;

      let createdComment: any;

      if (isGradeActivitiesViaAi) {
        const service = new UndergradGradesService();

        const response = await service.createActivityAnswer({
          body: text,
          questionId,
          activityType: ActivityType.DISCURSIVE_EXERCISE,
        });

        createdComment = {
          success: true,
          ...response,
        };
      } else {
        const response = await dexMutation(createUserThinkAndAnswerComment, {
          questionId,
          text,
        });

        createdComment = response.data.createUserThinkAndAnswerComment;
      }

      if (!createdComment.success) {
        ErrorHandler.showErrorMessage({
          error: new Error(i18n.t('itWasNotPossibleToCreateStudentComment').toString()),
        });

        return;
      }

      const normalizedResponse = normalize(createdComment, {
        comment: commentSchema,
        content: contentSchema,
        text: textSchema,
      });

      commit('SET_COMMENTS', normalizedResponse.entities);
      commit('SET_CONTENTS', normalizedResponse.entities);
      commit('SET_TEXTS', normalizedResponse.entities);
    } catch (e) {
      ErrorHandler.showErrorMessage({
        error: new Error(i18n.t('defaultErrorMessage').toString()),
      });
    }
  },

  async CREATE_COMMENT({ commit }, payload) {
    try {
      const { questionId, userId, text, attemptCount = null } = payload;

      const response = await dexMutation(createNestedCommentTextAndContent, {
        questionId,
        userId,
        text,
        attemptCount,
      });

      const normalizedResponse = normalize(
        response.data.createNestedCommentTextAndContent.nestedCommentTextAndContent,
        { comment: commentSchema },
      );

      commit('SET_COMMENTS', normalizedResponse.entities);
      commit('SET_CONTENTS', normalizedResponse.entities);
      commit('SET_TEXTS', normalizedResponse.entities);
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleToCreateUserComment').toString(),
      });
    }
  },

  async CREATE_EXERCISE_COMMENT({ commit }, payload) {
    try {
      const {
        questionId,
        body,
        attemptCount = null,
        attachments = [],
        isGradeActivitiesViaAi,
      } = payload;

      let createdComment: any;

      if (isGradeActivitiesViaAi) {
        const service = new UndergradGradesService();

        const response = await service.createActivityAnswer({
          body,
          activityType: ActivityType.PRACTICE_EXERCISE,
          questionId,
          attemptCount,
          attachments,
        });

        createdComment = {
          data: {
            createUserExerciseComment: {
              success: true,
              ...response,
            },
          },
        };
      } else {
        createdComment = await dexMutation(createUserDiscursiveExerciseComment, {
          questionId,
          body,
          attemptCount,
          attachments,
        });
      }

      const normalizedResponse = normalize(createdComment.data.createUserExerciseComment, {
        comment: commentSchema,
        content: contentSchema,
        text: textSchema,
        exerciseCommentAttachments: [exerciseCommentAttachmentsSchema],
      });

      commit('SET_COMMENTS', normalizedResponse.entities);
      commit('SET_CONTENTS', normalizedResponse.entities);
      commit('SET_TEXTS', normalizedResponse.entities);
      commit('SET_EXERCISE_COMMENT_ATTACHMENTS', normalizedResponse.entities);
    } catch (e) {
      ErrorHandler.showErrorMessage({
        error: new Error(i18n.t('defaultErrorMessage').toString()),
      });
    }
  },

  async UPDATE_EXERCISE_COMMENT({ commit }, payload) {
    try {
      const {
        commentId,
        questionId,
        body,
        textId,
        attachments = [],
        isGradeActivitiesViaAi,
      } = payload;

      let updatedComment: any;

      if (isGradeActivitiesViaAi) {
        const service = new UndergradGradesService();

        const response = await service.updateActivityAnswer({
          body,
          activityType: ActivityType.PRACTICE_EXERCISE,
          questionId,
          commentId,
          textId,
          attachments,
        });

        updatedComment = {
          success: true,
          ...response,
        };
      } else {
        const response = await dexMutation(updateUserPracticeActivityComment, {
          textId,
          questionId,
          body,
          commentId,
          attachments,
        });
        updatedComment = response.data.updateUserPracticeActivityComment;
      }

      if (!updatedComment.success) {
        ErrorHandler.showErrorMessage({
          error: new Error(i18n.t('itWasNotPossibleToUpdateStudentComment').toString()),
        });

        return;
      }

      const normalizedResponse = normalize(updatedComment, {
        comment: commentSchema,
        text: textSchema,
        exerciseCommentAttachments: [exerciseCommentAttachmentsSchema],
      });

      commit('SET_COMMENTS', normalizedResponse.entities);
      commit('SET_TEXTS', normalizedResponse.entities);
      commit('SET_EXERCISE_COMMENT_ATTACHMENTS', normalizedResponse.entities);
    } catch (e) {
      ErrorHandler.showErrorMessage({
        error: new Error(i18n.t('defaultErrorMessage').toString()),
      });
    }
  },

  async UPDATE_COMMENT_SCORE({ commit }, comment: UnifiedCommentModel) {
    try {
      commit('CHANGE_COMMENT_SCORE_UPDATING_STATUS', { commentId: comment.id, updating: true });

      await dexMutation(updateCommentById, {
        commentId: parseInt(comment.id, 10),
        score: comment.score,
      });

      commit('SET_UPDATED_COMMENT_SCORE', { commentId: comment.id, score: comment.score });
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleToUpdateStudentScore').toString(),
      });
    }

    commit('CHANGE_COMMENT_SCORE_UPDATING_STATUS', { commentId: comment.id, updating: false });
  },
};

export default actions;
